import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import getSafeApiBaseUrl from './apiBaseUrl';
import './KioskSetup.css';

const KIOSK_STORAGE_KEY = 'kiosk_uuid';
const KIOSK_SETUP_USER_KEY = 'kiosk_setup_user';
const HEARTBEAT_INTERVAL_MS = 2 * 60 * 1000;

// Browsers answer the first position request from whatever is cheapest — usually a Wi-Fi/IP estimate
// that can be kilometres off — and only sharpen to a true satellite fix a few seconds later. So we
// keep watching and hold on to the best reading instead of trusting the first one.
const GPS_TARGET_ACCURACY_M = 25;    // good enough to stop early
const GPS_MAX_ACCURACY_M = 150;      // must match KIOSK_MAX_LOCATION_ACCURACY_M on the server
const GPS_SAMPLE_WINDOW_MS = 20000;  // how long to keep refining before settling for the best so far
const COORDINATE_PRECISION = 6;      // ≈0.11m, finer than any GPS fix

function KioskSetup() {
    const baseUrl = getSafeApiBaseUrl();
    const [username, setUsername] = useState(() => localStorage.getItem(KIOSK_SETUP_USER_KEY) || '');
    const [password, setPassword] = useState('');
    const [sessionUser, setSessionUser] = useState(null);
    const [sessionChecked, setSessionChecked] = useState(false);
    const [kioskId, setKioskId] = useState(() => localStorage.getItem(KIOSK_STORAGE_KEY) || '');
    const [landmark, setLandmark] = useState('');
    const [city, setCity] = useState('');
    const [stateName, setStateName] = useState('');
    const [coordinates, setCoordinates] = useState('');
    const [locationAccuracyM, setLocationAccuracyM] = useState('');
    // 'GPS' once captured from the device, 'MANUAL' once a human edits the field. Sent to the server
    // so a hand-typed coordinate is never mistaken for a measured fix.
    const [locationSource, setLocationSource] = useState('');
    const [statusMessage, setStatusMessage] = useState('');
    const [loadingAction, setLoadingAction] = useState('');
    const [rememberMe, setRememberMe] = useState(true);
    const heartbeatInFlightRef = useRef(false);

    const isAuthenticated = Boolean(sessionUser);
    const isAccuracyTooCoarse = Boolean(locationAccuracyM) && Number(locationAccuracyM) > GPS_MAX_ACCURACY_M;
    // Block the save locally for a fix the server would reject anyway, so the operator recaptures
    // instead of getting a confusing round-trip error.
    const canSubmitSetup = useMemo(
        () => Boolean(kioskId.trim() && landmark.trim() && coordinates.trim()) && !isAccuracyTooCoarse,
        [kioskId, landmark, coordinates, isAccuracyTooCoarse]
    );

    const kioskFetch = useCallback(async (path, init = {}) => {
        const headers = { ...(init.headers || {}) };
        return fetch(`${baseUrl}${path}`, { ...init, credentials: 'include', headers });
    }, [baseUrl]);

    const login = async (event) => {
        event.preventDefault();
        setLoadingAction('login');
        setStatusMessage('');
        try {
            const response = await fetch(`${baseUrl}/api/kiosk-auth/login`, {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ username: username.trim(), password })
            });
            const payload = await response.json();
            if (!response.ok || !payload.success) {
                throw new Error(payload.message || payload.error || 'Login failed');
            }
            if (rememberMe) {
                localStorage.setItem(KIOSK_SETUP_USER_KEY, payload.username || username.trim());
            } else {
                localStorage.removeItem(KIOSK_SETUP_USER_KEY);
            }
            setSessionUser({ username: payload.username || username.trim() });
            setUsername(payload.username || username.trim());
            setPassword('');
            setStatusMessage('Setup login successful.');
        } catch (error) {
            setStatusMessage(error.message || 'Login failed');
        } finally {
            setLoadingAction('');
        }
    };

    const logout = async () => {
        try {
            await fetch(`${baseUrl}/api/kiosk-auth/logout`, {
                method: 'POST',
                credentials: 'include'
            });
        } catch (_error) {
            // Best-effort logout; local state still clears.
        }
        localStorage.removeItem(KIOSK_SETUP_USER_KEY);
        setSessionUser(null);
        setPassword('');
        setStatusMessage('Logged out from kiosk setup.');
    };

    const postHeartbeat = useCallback(async (savedKioskId, source) => {
        const response = await kioskFetch('/api/kiosk/heartbeat', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                kioskId: savedKioskId,
                appVersion: 'browser-test',
                deviceStatus: 'RUNNING',
                payload: {
                    source,
                    coordinates: coordinates || null
                }
            })
        });
        const payload = await response.json();
        if (!response.ok || !payload.success) {
            throw new Error(payload.message || payload.error || 'Failed to record heartbeat');
        }
        return payload;
    }, [coordinates, kioskFetch]);

    useEffect(() => {
        let cancelled = false;
        const bootstrapSession = async () => {
            try {
                const response = await fetch(`${baseUrl}/api/kiosk-auth/session`, {
                    credentials: 'include'
                });
                const payload = await response.json();
                if (response.ok && payload.success && payload.user) {
                    if (!cancelled) {
                        setSessionUser(payload.user);
                        if (payload.user.username) {
                            setUsername(payload.user.username);
                        }
                    }
                } else if (!cancelled) {
                    setSessionUser(null);
                }
            } catch (_error) {
                if (!cancelled) {
                    setSessionUser(null);
                }
            } finally {
                if (!cancelled) {
                    setSessionChecked(true);
                }
            }
        };

        bootstrapSession();
        return () => {
            cancelled = true;
        };
    }, [baseUrl]);

    const captureLocation = () => {
        if (!navigator.geolocation) {
            setStatusMessage('Geolocation is not supported in this browser.');
            return;
        }
        setLoadingAction('location');
        setStatusMessage('Acquiring GPS fix...');

        let best = null;
        let watchId = null;
        let timerId = null;
        let settled = false;

        const teardown = () => {
            if (watchId !== null) navigator.geolocation.clearWatch(watchId);
            if (timerId !== null) window.clearTimeout(timerId);
            setLoadingAction('');
        };

        const finish = () => {
            if (settled) return;
            settled = true;
            teardown();

            if (!best) {
                setStatusMessage('Could not get a GPS fix. Check that location access is allowed for this site.');
                return;
            }
            const accuracy = Math.round(best.accuracy);
            setCoordinates(`${best.latitude.toFixed(COORDINATE_PRECISION)},${best.longitude.toFixed(COORDINATE_PRECISION)}`);
            setLocationAccuracyM(String(accuracy));
            setLocationSource('GPS');
            setStatusMessage(accuracy > GPS_MAX_ACCURACY_M
                ? `Best fix was only ±${accuracy}m — too coarse to save. That is a Wi-Fi/IP estimate rather than GPS, and can be kilometres off. Move the device near a window or use a GPS-capable device, then capture again.`
                : `Location captured (±${accuracy}m).`);
        };

        watchId = navigator.geolocation.watchPosition(
            (position) => {
                const { latitude, longitude, accuracy } = position.coords;
                if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return;
                if (!best || accuracy < best.accuracy) {
                    best = { latitude, longitude, accuracy };
                    setLocationAccuracyM(String(Math.round(accuracy)));
                    setStatusMessage(`Refining GPS fix... currently ±${Math.round(accuracy)}m`);
                }
                if (best.accuracy <= GPS_TARGET_ACCURACY_M) finish();
            },
            (error) => {
                if (settled) return;
                settled = true;
                teardown();
                setStatusMessage(error.message || 'Unable to capture current location.');
            },
            { enableHighAccuracy: true, timeout: GPS_SAMPLE_WINDOW_MS, maximumAge: 0 }
        );
        // Satellite lock can take longer than the target allows; settle for the best reading so far.
        timerId = window.setTimeout(finish, GPS_SAMPLE_WINDOW_MS);
    };

    const setupKiosk = async (event) => {
        event.preventDefault();
        if (!canSubmitSetup) return;
        setLoadingAction('setup');
        setStatusMessage('');
        try {
            const response = await kioskFetch('/api/kiosk/setup', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    kioskId: kioskId.trim().toUpperCase(),
                    landmark: landmark.trim(),
                    city: city.trim(),
                    state: stateName.trim(),
                    coordinates: coordinates.trim(),
                    locationAccuracyM: locationAccuracyM ? Number(locationAccuracyM) : null,
                    locationSource: locationSource || 'MANUAL'
                })
            });
            const payload = await response.json();
            if (!response.ok || !payload.success) {
                throw new Error(payload.message || payload.error || 'Failed to register kiosk');
            }
            localStorage.setItem(KIOSK_STORAGE_KEY, kioskId.trim().toUpperCase());
            setKioskId(kioskId.trim().toUpperCase());
            setStatusMessage(payload.message || 'Kiosk registered successfully.');
        } catch (error) {
            setStatusMessage(error.message || 'Failed to register kiosk');
        } finally {
            setLoadingAction('');
        }
    };

    const sendHeartbeat = async () => {
        const savedKioskId = (localStorage.getItem(KIOSK_STORAGE_KEY) || kioskId).trim();
        if (!savedKioskId) {
            setStatusMessage('Register a kiosk first so heartbeat knows which kiosk to ping.');
            return;
        }
        setLoadingAction('heartbeat');
        setStatusMessage('');
        try {
            await postHeartbeat(savedKioskId, 'kiosk-setup-ui');
            setStatusMessage('Heartbeat recorded successfully.');
        } catch (error) {
            setStatusMessage(error.message || 'Failed to record heartbeat');
        } finally {
            setLoadingAction('');
        }
    };

    useEffect(() => {
        if (!isAuthenticated) return undefined;
        const savedKioskId = (localStorage.getItem(KIOSK_STORAGE_KEY) || kioskId).trim();
        if (!savedKioskId) return undefined;

        let cancelled = false;
        const sendAutoHeartbeat = async () => {
            if (heartbeatInFlightRef.current) return;
            heartbeatInFlightRef.current = true;
            try {
                await postHeartbeat(savedKioskId, 'kiosk-setup-ui-auto');
            } catch (error) {
                if (!cancelled) {
                    setStatusMessage(error.message || 'Failed to record automatic heartbeat');
                }
            } finally {
                heartbeatInFlightRef.current = false;
            }
        };

        sendAutoHeartbeat();
        const intervalId = window.setInterval(sendAutoHeartbeat, HEARTBEAT_INTERVAL_MS);
        return () => {
            cancelled = true;
            window.clearInterval(intervalId);
        };
    }, [baseUrl, coordinates, isAuthenticated, kioskId, postHeartbeat]);

    if (!sessionChecked) {
        return (
            <div className="kiosk-setup-page">
                <div className="kiosk-login-stage">
                    <div className="kiosk-login-room">
                        <div className="kiosk-login-card">
                            <div className="kiosk-login-chip">Kiosk Setup</div>
                            <h1 className="kiosk-login-title">Checking session...</h1>
                            <p className="kiosk-login-subtitle">Verifying the kiosk setup session securely before showing controls.</p>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="kiosk-setup-page">
            {!isAuthenticated ? (
                <div className="kiosk-login-stage">
                    <div className="kiosk-login-room">
                        <div className="kiosk-login-card">
                            <div className="kiosk-login-chip">Kiosk Setup Login</div>
                            <h1 className="kiosk-login-title">Welcome Back!</h1>
                            <p className="kiosk-login-subtitle">Authorized staff sign in to register a kiosk and capture its live location.</p>

                            <form className="kiosk-login-form" onSubmit={login}>
                                <label className="kiosk-login-field">
                                    <span>Username</span>
                                    <input value={username} onChange={(event) => setUsername(event.target.value)} placeholder="kiosksetup" />
                                </label>
                                <label className="kiosk-login-field">
                                    <span>Password</span>
                                    <input type="password" value={password} onChange={(event) => setPassword(event.target.value)} placeholder="Enter password" />
                                </label>

                                <div className="kiosk-login-meta">
                                    <label className="kiosk-remember">
                                        <input type="checkbox" checked={rememberMe} onChange={(event) => setRememberMe(event.target.checked)} />
                                        <span>Remember me</span>
                                    </label>
                                    <button type="button" className="kiosk-link-button" onClick={() => setStatusMessage('Contact Super Admin to reset kiosk setup credentials.')}>
                                        Forgot password?
                                    </button>
                                </div>

                                <button type="submit" className="kiosk-login-submit" disabled={loadingAction === 'login' || !username.trim() || !password}>
                                    {loadingAction === 'login' ? 'Signing In...' : 'Login To Continue'}
                                </button>
                            </form>

                            <div className="kiosk-login-divider"><span>or</span></div>
                            <div className="kiosk-login-alt">Default setup user is seeded automatically when `kiosk.kiosk_setup_users` is empty.</div>

                            {statusMessage && <div className="kiosk-status-box kiosk-status-box-login">{statusMessage}</div>}
                        </div>
                    </div>
                </div>
            ) : (
                <div className="kiosk-setup-shell kiosk-setup-shell-narrow">
                    <div className="kiosk-hero kiosk-hero-compact">
                        <div className="kiosk-eyebrow">Kiosk Setup</div>
                        <h1>Simple kiosk registration.</h1>
                        <p>Authorized staff can now capture the kiosk location from the browser and register the kiosk on the shared backend.</p>
                    </div>
                    <>
                        <form className="kiosk-card kiosk-card-compact" onSubmit={setupKiosk}>
                            <div className="kiosk-card-heading">
                                <h2>Register Kiosk</h2>
                                <span>{username}</span>
                            </div>

                            <label className="kiosk-field">
                                <span>Kiosk ID</span>
                                <input value={kioskId} onChange={(event) => setKioskId(event.target.value.toUpperCase())} placeholder="KIOSK-MUM-001" />
                            </label>
                            <label className="kiosk-field">
                                <span>Branch / Landmark</span>
                                <input value={landmark} onChange={(event) => setLandmark(event.target.value)} placeholder="Andheri East Branch" />
                            </label>
                            <div className="kiosk-field-grid kiosk-field-grid-compact">
                                <label className="kiosk-field">
                                    <span>City</span>
                                    <input value={city} onChange={(event) => setCity(event.target.value)} placeholder="Mumbai" />
                                </label>
                                <label className="kiosk-field">
                                    <span>State</span>
                                    <input value={stateName} onChange={(event) => setStateName(event.target.value)} placeholder="Maharashtra" />
                                </label>
                            </div>
                            <label className="kiosk-field">
                                <span>Coordinates</span>
                                <input
                                    value={coordinates}
                                    onChange={(event) => {
                                        setCoordinates(event.target.value);
                                        // Hand-edited: drop the measured accuracy so this is never
                                        // recorded as a GPS fix it no longer matches.
                                        setLocationSource('MANUAL');
                                        setLocationAccuracyM('');
                                    }}
                                    placeholder="19.113600,72.869700"
                                />
                            </label>

                            <div className="kiosk-action-row kiosk-action-row-split">
                                <button type="button" className="kiosk-btn kiosk-btn-secondary" onClick={captureLocation} disabled={loadingAction === 'location'}>
                                    {loadingAction === 'location' ? 'Capturing...' : 'Use Current Location'}
                                </button>
                                <button type="submit" className="kiosk-btn" disabled={loadingAction === 'setup' || !canSubmitSetup}>
                                    {loadingAction === 'setup' ? 'Saving...' : 'Save Kiosk'}
                                </button>
                            </div>

                            {locationAccuracyM && (
                                <div className="kiosk-meta-chip">
                                    GPS accuracy ±{locationAccuracyM}m
                                    {isAccuracyTooCoarse ? ' — too coarse to save, capture again' : ''}
                                </div>
                            )}
                            {!locationAccuracyM && coordinates.trim() && (
                                <div className="kiosk-meta-chip">Manually entered — not a measured GPS fix</div>
                            )}
                            {statusMessage && <div className="kiosk-status-box">{statusMessage}</div>}
                        </form>

                        <div className="kiosk-card kiosk-card-compact">
                            <div className="kiosk-card-heading">
                                <h2>Quick Actions</h2>
                                <span>Stored kiosk: {localStorage.getItem(KIOSK_STORAGE_KEY) || 'none'}</span>
                            </div>
                            <div className="kiosk-action-row kiosk-action-row-split">
                                <button type="button" className="kiosk-btn" onClick={sendHeartbeat} disabled={loadingAction === 'heartbeat'}>
                                    {loadingAction === 'heartbeat' ? 'Sending...' : 'Send Heartbeat'}
                                </button>
                                <button type="button" className="kiosk-btn kiosk-btn-ghost" onClick={logout}>
                                    Logout
                                </button>
                            </div>
                        </div>
                    </>
                </div>
            )}
        </div>
    );
}

export default KioskSetup;
